/**
 * Formats a number as a currency string in GBP.
 * Negative values are automatically shown with parentheses.
 *
 * Example:
 *   1234.56  -> "£1,234.56"
 *   -1234.56 -> "(£1,234.56)"
 *
 * @param value - The number to format.
 * @returns The formatted currency string, or an empty string if invalid.
 */
export const formatCurrency = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '';
  }
  try {
    // For Pain/Gain Amount, we want to show positive numbers for gains
    // and negative numbers for pain (losses)
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      signDisplay: 'auto',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return value.toString();
  }
};

/**
 * Formats a number using the en-US locale with exactly 2 decimal places.
 *
 * Example:
 *   1234567     -> "1,234,567.00"
 *   1234567.89  -> "1,234,567.89"
 *
 * @param value - The number to format.
 * @returns The formatted number as a string, or an empty string if invalid.
 */
export const formatValue = (value: number | null): string => {
  if (value === null || typeof value !== 'number' || isNaN(value)) {
    return '';
  }
  try {
    // Handle negative numbers by preserving the minus sign
    const isNegative = value < 0;
    const absoluteValue = Math.abs(value);
    
    // Format with commas and 2 decimal places
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay: 'negative',
    }).format(value);
  } catch (error) {
    console.error('Error formatting value:', error);
    return value.toString();
  }
};

/**
 * Formats a number as a percentage string.
 * Assumes the provided value is a whole number (e.g. 25 for 25%),
 * so it divides the value by 100 for correct formatting.
 *
 * Example:
 *   25 -> "25.00%"
 *
 * @param value - The number to format.
 * @returns The formatted percentage string, or an empty string if invalid.
 */
export const formatPercentage = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '';
  }
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100);
  } catch (error) {
    console.error('Error formatting percentage:', error);
    return value.toString();
  }
};

/**
 * Formats a number as a ratio with 2 decimal places using the en-GB locale.
 *
 * Example:
 *   1.2345 -> "1.23"
 *
 * @param value - The number to format.
 * @returns The formatted ratio as a string, or an empty string if invalid.
 */
export const formatRatio = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '';
  }
  try {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    console.error('Error formatting ratio:', error);
    return value.toString();
  }
};
